import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { authService, type TOTPEnableResponse } from '@/services/authService'
import QRCode from 'qrcode'

interface TOTPEnableModalProps {
  onSuccess: () => void
}

export interface TOTPEnableModalRef {
  open: () => void
  close: () => void
}

const TOTPEnableModal = forwardRef<TOTPEnableModalRef, TOTPEnableModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('settings')
    const [isOpen, setIsOpen] = useState(false)
    const [step, setStep] = useState<1 | 2>(1)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    // Step 1 data
    const [totpData, setTotpData] = useState<TOTPEnableResponse | null>(null)
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState('')

    // Step 2 data
    const [totpCode, setTotpCode] = useState('')
    const [isVerifying, setIsVerifying] = useState(false)

    useImperativeHandle(ref, () => ({
      open: async () => {
        setIsOpen(true)
        setStep(1)
        setError('')
        setTotpCode('')
        setTotpData(null)
        setQrCodeDataUrl('')
        await fetchTOTPSetup()
      },
      close: () => {
        if (!loading && !isVerifying) {
          setIsOpen(false)
          setStep(1)
          setTotpData(null)
          setQrCodeDataUrl('')
          setTotpCode('')
          setError('')
        }
      },
    }))

    const fetchTOTPSetup = async () => {
      try {
        setLoading(true)
        setError('')

        const data = await authService.enableTOTP()
        setTotpData(data)

        // Generate QR code
        const qrDataUrl = await QRCode.toDataURL(data.qr_code_url, {
          width: 192,
          margin: 2,
        })
        setQrCodeDataUrl(qrDataUrl)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
        if (errorMessage.startsWith('errors.')) {
          setError(t(errorMessage, errorMessage))
        } else {
          setError(errorMessage)
        }
      } finally {
        setLoading(false)
      }
    }

    const handleCopySecret = () => {
      if (totpData?.secret) {
        navigator.clipboard.writeText(totpData.secret)
      }
    }

    const handleCopyRecoveryCodes = () => {
      if (totpData?.recovery_codes) {
        navigator.clipboard.writeText(totpData.recovery_codes.join('\n'))
      }
    }

    const handleNextStep = () => {
      setStep(2)
      setError('')
    }

    const handleVerify = async () => {
      if (!totpCode || totpCode.length !== 6) {
        setError(t('profile.2fa.errors.invalidCode', 'Please enter a 6-digit code'))
        return
      }

      if (!totpData) {
        setError(t('errors.operationFailed', 'Operation failed'))
        return
      }

      try {
        setIsVerifying(true)
        setError('')

        await authService.verifyEnableTOTP({
          recovery_codes: totpData.recovery_codes,
          secret: totpData.secret,
          totp_code: totpCode,
        })

        setIsOpen(false)
        onSuccess()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
        if (errorMessage.startsWith('errors.')) {
          setError(t(errorMessage, errorMessage))
        } else {
          setError(errorMessage)
        }
      } finally {
        setIsVerifying(false)
      }
    }

    const handleClose = () => {
      if (!loading && !isVerifying) {
        setIsOpen(false)
      }
    }

    if (!isOpen) return null

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
          {/* Header - Sticky */}
          <div className="sticky top-0 z-10 px-6 py-4 border-b border-gray-200 bg-white rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {t('profile.2fa.modal.title', 'Enable Two-Factor Authentication')}
                  </h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {t('profile.2fa.modal.subtitle', `Step ${step} of 2`)}
                  </p>
                </div>
              </div>
              {!loading && !isVerifying && (
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2.5">
                <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700 flex-1">{error}</p>
              </div>
            )}

            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-gray-600">{t('profile.2fa.modal.loading', 'Setting up 2FA...')}</p>
              </div>
            ) : step === 1 && totpData ? (
              /* Step 1: Show QR Code and Secret */
              <div className="space-y-4">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    {t('profile.2fa.modal.step1.title', 'Scan QR Code')}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {t('profile.2fa.modal.step1.description', 'Scan with your authenticator app:')}
                  </p>
                  <div className="flex justify-center mb-4">
                    {qrCodeDataUrl && (
                      <div className="p-3 bg-white border-2 border-gray-200 rounded-xl">
                        <img src={qrCodeDataUrl} alt="QR Code" className="w-48 h-48" />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    {t('profile.2fa.modal.step1.manualTitle', 'Manual Entry')}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    {t('profile.2fa.modal.step1.manualDescription', 'Or enter this code manually:')}
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm">
                      {totpData.secret}
                    </code>
                    <button
                      onClick={handleCopySecret}
                      className="px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all"
                      title={t('profile.2fa.modal.copy', 'Copy')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    {t('profile.2fa.modal.recoveryCodes.title', 'Recovery Codes')}
                  </h4>
                  <p className="text-sm text-amber-800 mb-2">
                    {t('profile.2fa.modal.recoveryCodes.description', 'Save these codes securely for account recovery.')}
                  </p>
                  <div className="bg-white border border-amber-200 rounded-lg p-2 mb-2">
                    <div className="grid grid-cols-3 gap-1.5 font-mono text-xs">
                      {totpData.recovery_codes.map((code, index) => (
                        <div key={index} className="text-gray-900">
                          {code}
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={handleCopyRecoveryCodes}
                    className="text-sm text-amber-900 font-medium hover:text-amber-800 underline"
                  >
                    {t('profile.2fa.modal.recoveryCodes.copy', 'Copy all codes')}
                  </button>
                </div>
              </div>
            ) : step === 2 ? (
              /* Step 2: Verify Code */
              <div className="space-y-4">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    {t('profile.2fa.modal.step2.title', 'Verify Setup')}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {t('profile.2fa.modal.step2.description', 'Enter the 6-digit code from your authenticator app:')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('profile.2fa.modal.step2.codeLabel', 'Verification Code')}
                  </label>
                  <input
                    type="text"
                    value={totpCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                      setTotpCode(value)
                      setError('')
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-center text-xl font-mono tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    disabled={isVerifying}
                    autoFocus
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex gap-2">
                    <svg className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <p className="text-xs text-blue-800">
                      {t('profile.2fa.modal.step2.note', 'Save your recovery codes before proceeding.')}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Footer - Sticky */}
          {!loading && (
            <div className="sticky bottom-0 z-10 border-t border-gray-200/60 px-5 py-3 bg-white/80 backdrop-blur-xl flex items-center justify-between rounded-b-2xl shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
              <button
                onClick={handleClose}
                disabled={isVerifying}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('profile.2fa.modal.cancel', 'Cancel')}
              </button>

              {step === 1 ? (
                <button
                  onClick={handleNextStep}
                  className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all font-medium shadow-sm hover:shadow"
                >
                  {t('profile.2fa.modal.next', 'Next Step')}
                </button>
              ) : (
                <button
                  onClick={handleVerify}
                  disabled={isVerifying || totpCode.length !== 6}
                  className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-sm hover:shadow"
                >
                  {isVerifying && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>{isVerifying ? t('profile.2fa.modal.verifying', 'Verifying...') : t('profile.2fa.modal.enable', 'Enable 2FA')}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }
)

TOTPEnableModal.displayName = 'TOTPEnableModal'

export default TOTPEnableModal
